'use client';
import SearchInput from '@/components/common/SearchInput';
import React, { useCallback, useEffect, useState, useMemo } from 'react';
import { usePathname } from 'next/navigation';
import { exportSearchExecutionResult, getSearchExecution } from '@/api/requests/searchExecutions';
import { SearchExecution } from '@/actions/dtos/search/searchExecution';
import { useAuthenticated } from '@/hooks/useAuthenticated';
import CreateAgentModal from '@/components/search/agents/CreateAgentModal';
import { faDownload, faFloppyDisks } from '@fortawesome/pro-duotone-svg-icons';
import Button from '@/components/common/Button';
import { SubscriptionTier } from '@/actions/dtos/subscription';
import { downloadFile } from '@/utils/downloadUtil';
import { useAccount } from '@/hooks/useAccount';
import { useDispatch } from 'react-redux';
import { openModal, FeatureType } from '@/slices/features/modalSlice';
import { useSubscriptionLimits } from '@/hooks/useSubscriptionLimits';
import SubHeaderMobileMenu from '@/components/header/SubHeaderMobileMenu';

const SubHeader = () => {
  const { isAuthenticated } = useAuthenticated();
  const pathname = usePathname();
  const { isAgentLimitReached } = useSubscriptionLimits();
  const [searchExecution, setSearchExecution] = useState<SearchExecution>();
  const [isExportLoading, setIsExportLoading] = useState(false);
  const [isCreateAgentModalOpen, setIsCreateAgentModalOpen] = useState<boolean>(false);
  const dispatch = useDispatch();
  const [hasPermissionToExport, setHasPermissionToExport] = useState(false);
  const { currentTier } = useAccount();

  const searchExecutionId = useMemo(() => {
    const parts = pathname!.split('/');
    return parts[1] === 'suche' && parts[2] ? parts[2] : undefined;
  }, [pathname]);

  const fetchSearchExecution = useCallback(async () => {
    if (!searchExecutionId) return;
    const response = await getSearchExecution(searchExecutionId, !isAuthenticated);
    setSearchExecution(response as SearchExecution);
  }, [searchExecutionId, isAuthenticated]);

  useEffect(() => {
    fetchSearchExecution();
  }, [fetchSearchExecution]);

  useEffect(() => {
    setHasPermissionToExport(isAuthenticated && currentTier !== SubscriptionTier.BASIC);
  }, [isAuthenticated, currentTier]);

  const openCreateAgentModal = () => {
    if (isAgentLimitReached) {
      dispatch(openModal(FeatureType.MORE_SEARCH_PROFILES));
    } else {
      setIsCreateAgentModalOpen(true);
    }
  };

  const exportSearchResult = async () => {
    try {
      setIsExportLoading(true);
      const { blob, fileName } = await exportSearchExecutionResult(searchExecutionId);
      downloadFile(blob, fileName);
    } finally {
      setIsExportLoading(false);
    }
  };

  const handleExportButtonClick = async () => {
    if (hasPermissionToExport) {
      await exportSearchResult();
    } else {
      dispatch(openModal(FeatureType.EXCEL_EXPORT));
    }
  };

  return (
    <header className={`${!isAuthenticated ? 'sticky lg:top-[68px]' : ''} z-40 bg-primary py-1.5 shadow-lg`}>
      <div className="page-layout flex items-center justify-between">
        <div className="flex items-center">
          <SearchInput query={searchExecution?.query} executionId={searchExecutionId} />
        </div>
        <div className="flex items-center">
          <div className="hidden items-center xl:flex">
            {isAuthenticated && searchExecutionId && (
              <Button
                label="Als Suchprofil speichern"
                buttonClassName="whitespace-nowrap px-1 border-white hover:border-white mr-3 !min-h-2 !h-9 px-2.5"
                variant="secondary"
                titleClassName="text-sm font-semibold max-md:hidden"
                onClick={openCreateAgentModal}
                icon={faFloppyDisks}
                locked={isAgentLimitReached}
              />
            )}
            {searchExecutionId && (
              <Button
                label="Suchergebnisse exportieren"
                buttonClassName="whitespace-nowrap px-1 border-white hover:border-white !min-h-2 !h-9 px-2.5"
                variant="secondary"
                titleClassName="text-sm font-semibold max-md:hidden"
                onClick={handleExportButtonClick}
                icon={faDownload}
                locked={!hasPermissionToExport}
                isLoading={isExportLoading}
              />
            )}
          </div>
          <SubHeaderMobileMenu
            isAuthenticated={isAuthenticated}
            openCreateAgentModal={openCreateAgentModal}
            isAgentLimitReached={isAgentLimitReached}
            handleExportButtonClick={handleExportButtonClick}
            hasPermissionToExport={hasPermissionToExport}
            searchExecutionId={searchExecutionId}
          />
        </div>
      </div>
      <CreateAgentModal
        isOpen={isCreateAgentModalOpen}
        onClose={() => setIsCreateAgentModalOpen(false)}
        query={searchExecution?.query}
      />
    </header>
  );
};

export default SubHeader;
